import React from 'react';

const CompanyCounter = ({ companies }) => {
  const companyCount = companies.length;

  // Definice cílů
  const MIN_TARGET = 200;
  const OPTIMAL_MIN = 600;
  const OPTIMAL_MAX = 800;
  
  // Výpočet barvy podle počtu firem
  const getColor = (count) => {
    if (count < MIN_TARGET) {
      // 0-200: červená
      return '#ef4444'; // red-500
    } else if (count >= MIN_TARGET && count < OPTIMAL_MIN) {
      // 200-600: postupný přechod od červené k zelené
      const progress = (count - MIN_TARGET) / (OPTIMAL_MIN - MIN_TARGET);
      const red = Math.round(239 - (239 - 34) * progress); // 239 -> 34
      const green = Math.round(68 + (197 - 68) * progress); // 68 -> 197
      const blue = Math.round(68 + (94 - 68) * progress);   // 68 -> 94
      return `rgb(${red}, ${green}, ${blue})`;
    } else if (count >= OPTIMAL_MIN && count <= OPTIMAL_MAX) {
      // 600-800: zelená (optimální rozsah)
      return '#22c55e'; // green-500
    } else {
      // Nad 800: postupný přechod zpět k červené
      const overTarget = count - OPTIMAL_MAX;
      const maxOver = 200; // Předpokládáme, že nad 1000 je už úplně červené
      const progress = Math.min(overTarget / maxOver, 1);
      const red = Math.round(34 + (239 - 34) * progress);   // 34 -> 239
      const green = Math.round(197 - (197 - 68) * progress); // 197 -> 68
      const blue = Math.round(94 - (94 - 68) * progress);    // 94 -> 68
      return `rgb(${red}, ${green}, ${blue})`;
    }
  };

  // Výpočet pozice na progress baru
  const getProgressPosition = (count) => {
    if (count <= MIN_TARGET) {
      // 0-200: 0-25% na baru
      return (count / MIN_TARGET) * 25;
    } else if (count <= OPTIMAL_MIN) {
      // 200-600: 25-60% na baru (začátek zelené zóny je na 60%)
      const progress = (count - MIN_TARGET) / (OPTIMAL_MIN - MIN_TARGET);
      return 25 + progress * 35;
    } else if (count <= OPTIMAL_MAX) {
      // 600-800: 60-80% na baru (zelená zóna)
      const progress = (count - OPTIMAL_MIN) / (OPTIMAL_MAX - OPTIMAL_MIN);
      return 60 + progress * 20;
    } else {
      // Nad 800: 80-100% na baru
      const overTarget = Math.min(count - OPTIMAL_MAX, 200);
      const progress = overTarget / 200;
      return 80 + progress * 20;
    }
  };

  // Gradient pro progress bar
  const progressGradient = `linear-gradient(90deg, 
    #ef4444 0%,      /* červená na začátku */
    #f97316 25%,     /* oranžová */
    #eab308 50%,     /* žlutá */
    #22c55e 60%,     /* zelená - začátek optimálního rozsahu (600 firem) */
    #22c55e 80%,     /* zelená - konec optimálního rozsahu (800 firem) */
    #ef4444 100%     /* červená - překročení cíle */
  )`;

  const currentColor = getColor(companyCount);
  const progressPosition = getProgressPosition(companyCount);

  const getStatusText = (count) => {
    if (count < MIN_TARGET) {
      return `Potřeba ${MIN_TARGET - count} firem`;
    } else if (count >= MIN_TARGET && count < OPTIMAL_MIN) {
      return `Rostoucí databáze`;
    } else if (count >= OPTIMAL_MIN && count <= OPTIMAL_MAX) {
      return `Optimální stav`;
    } else {
      return `Příliš mnoho firem`;
    }
  };

  return (
    <div className="bg-white p-4 rounded shadow-sm w-full h-[300px]">
      <h3 className="text-sm font-bold text-gray-700 mb-3 text-center">Počet firem v CRM</h3>
      
      {/* Hlavní číslo a popis */}
      <div className="text-center mb-3">
        <div 
          className="text-4xl font-bold mb-1"
          style={{ color: currentColor }}
        >
          {companyCount}
        </div>
        <div className="text-sm text-gray-600">
          {getStatusText(companyCount)}
        </div>
      </div>

      {/* Progress bar integrovaný do boxu */}
      <div className="mt-3">
        {/* Popisky nad progress barem */}
        <div className="flex justify-between text-sm text-gray-400 mb-1">
          <span>0</span>
          <span>1000+</span>
        </div>

        {/* Progress bar */}
        <div className="relative">
          {/* Pozadí s gradientem */}
          <div 
            className="h-3 rounded-full"
            style={{ background: progressGradient, opacity: 0.6 }}
          ></div>
          
          {/* Ukazatel aktuální pozice */}
          <div 
            className="absolute top-0 h-3 w-2 bg-gray-800 rounded-full transform -translate-x-1/2"
            style={{ 
              left: `${Math.min(progressPosition, 100)}%`,
              boxShadow: '0 0 6px rgba(0,0,0,0.6)'
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default CompanyCounter;
